import React, { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table'
import { Trash2, Pencil } from 'lucide-react'

interface SubjectOption { id: number; name: string; code: string }
interface StandardOption { id: number; name: string }
interface Chapter {
  id: number
  name: string
  number: number | null
  ncert_file: string | null
  subject_id: number
  subject_name: string
  standard_ids: number[]
  standard_names: string
}
interface Props {
  chapters: Chapter[]
  subjects: SubjectOption[]
  standards: StandardOption[]
}

export default function ChaptersIndex({ chapters, subjects, standards }: Props) {
  const [name, setName] = useState('')
  const [number, setNumber] = useState<number | ''>('')
  const [subjectId, setSubjectId] = useState<number | ''>(subjects[0]?.id ?? '')
  const [standardIds, setStandardIds] = useState<number[]>([])
  const [ncertFile, setNcertFile] = useState('')

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editNumber, setEditNumber] = useState<number | ''>('')
  const [editSubjectId, setEditSubjectId] = useState<number | ''>('')
  const [editStandardIds, setEditStandardIds] = useState<number[]>([])
  const [editNcertFile, setEditNcertFile] = useState('')

  const handleCreate = () => {
    if (!subjectId) return
    router.post('/admin/chapters', {
      chapter: {
        name,
        number: number === '' ? null : number,
        subject_id: subjectId,
        standard_ids: standardIds,
        ncert_file: ncertFile || null
      }
    }, {
      onSuccess: () => {
        setName('')
        setNumber('')
        setStandardIds([])
        setNcertFile('')
      }
    })
  }

  const startEdit = (c: Chapter) => {
    setEditingId(c.id)
    setEditName(c.name)
    setEditNumber(c.number ?? '')
    setEditSubjectId(c.subject_id)
    setEditStandardIds(c.standard_ids || [])
    setEditNcertFile(c.ncert_file ?? '')
  }

  const handleUpdate = (id: number) => {
    router.patch(`/admin/chapters/${id}`, {
      chapter: {
        name: editName,
        number: editNumber === '' ? null : editNumber,
        subject_id: editSubjectId,
        standard_ids: editStandardIds,
        ncert_file: editNcertFile || null
      }
    }, { onSuccess: () => setEditingId(null) })
  }

  const handleDelete = (id: number) => {
    if (!confirm('Are you sure?')) return
    router.delete(`/admin/chapters/${id}`)
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Taxonomy Management</h1>
          <p className="text-muted-foreground mt-1">Manage standards, subjects, chapters, sections, exams, and syllabi</p>
        </div>

        {/* Create form */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Add Chapter</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={subjectId}
                onChange={(e) => setSubjectId(Number(e.target.value))}
              >
                <option value="" disabled>Select Subject *</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                ))}
              </select>

              <Input
                type="number"
                placeholder="Chapter # (e.g. 1)"
                value={number}
                onChange={(e) => setNumber(e.target.value ? Number(e.target.value) : '')}
                className="w-full"
              />

              <Input
                placeholder="Chapter Name (e.g. Kinematics)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="md:col-span-2"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Applicable Standards:</label>
              <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/20">
                {standards.map((st) => (
                  <label key={st.id} className="flex items-center gap-1.5 text-xs cursor-pointer p-1 rounded hover:bg-muted">
                    <input
                      type="checkbox"
                      checked={standardIds.includes(st.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setStandardIds([...standardIds, st.id])
                        } else {
                          setStandardIds(standardIds.filter((id) => id !== st.id))
                        }
                      }}
                    />
                    <span>{st.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleCreate} disabled={!name.trim() || !subjectId}>
                Add Chapter
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Chapters ({chapters.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Standards</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {chapters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                      No chapters created yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  chapters.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        {editingId === c.id ? (
                          <Input
                            type="number"
                            value={editNumber}
                            onChange={(e) => setEditNumber(e.target.value ? Number(e.target.value) : '')}
                            className="w-16"
                          />
                        ) : (c.number ?? '—')}
                      </TableCell>
                      <TableCell>
                        {editingId === c.id ? (
                          <select
                            className="flex h-9 rounded-md border border-input bg-background px-2 py-1 text-sm"
                            value={editSubjectId}
                            onChange={(e) => setEditSubjectId(Number(e.target.value))}
                          >
                            {subjects.map((s) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        ) : c.subject_name}
                      </TableCell>
                      <TableCell>
                        {editingId === c.id ? (
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {standards.map((st) => (
                              <label key={st.id} className="flex items-center gap-1 text-[11px]">
                                <input
                                  type="checkbox"
                                  checked={editStandardIds.includes(st.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setEditStandardIds([...editStandardIds, st.id])
                                    } else {
                                      setEditStandardIds(editStandardIds.filter((id) => id !== st.id))
                                    }
                                  }}
                                />
                                <span>{st.name}</span>
                              </label>
                            ))}
                          </div>
                        ) : (c.standard_names || '—')}
                      </TableCell>
                      <TableCell className="font-medium">
                        {editingId === c.id ? (
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                          />
                        ) : c.name}
                      </TableCell>
                      <TableCell className="text-right">
                        {editingId === c.id ? (
                          <div className="flex justify-end gap-1">
                            <Button size="sm" onClick={() => handleUpdate(c.id)}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => startEdit(c)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDelete(c.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
